import React, { useState, useEffect } from 'react';
import {
  Circle,
  Camera,
  Clipboard,
  Usb,
  AlertTriangle,
  Wifi,
} from 'lucide-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useWebSocket } from '../../hooks/useWebSocket';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';

dayjs.extend(relativeTime);

function LiveEventFeed({ activities, autoRefresh = true }) {
  const [newEventsCount, setNewEventsCount] = useState(0);
  const [lastActivityId, setLastActivityId] = useState(null);
  const [realtimeEvents, setRealtimeEvents] = useState([]);

  const { connected } = useWebSocket((data) => {
    if (data.event_type) {
      setRealtimeEvents((prev) => [data, ...prev].slice(0, 20));
      setNewEventsCount((prev) => prev + 1);
    }
  });

  useEffect(() => {
    if (activities.length > 0) {
      const latestId = activities[0].timestamp;
      if (lastActivityId && latestId !== lastActivityId) {
        setNewEventsCount((prev) => prev + 1);
      }
      setLastActivityId(latestId);
    }
  }, [activities, lastActivityId]);

  const getEventIcon = (type) => {
    switch (type) {
      case 'screenshot':
        return <Camera className="h-4 w-4 text-primary" />;
      case 'clipboard':
        return <Clipboard className="h-4 w-4 text-primary" />;
      case 'usb':
        return <Usb className="h-4 w-4 text-warning" />;
      case 'incident':
        return <AlertTriangle className="h-4 w-4 text-destructive" />;
      default:
        return <Circle className="h-3 w-3 text-muted-foreground" />;
    }
  };

  const getRiskVariant = (score) => {
    if (score >= 80) return 'destructive';
    if (score >= 50) return 'warning';
    if (score >= 20) return 'default';
    return 'success';
  };

  const combinedEvents = [...realtimeEvents, ...activities].slice(0, 15);

  return (
    <Card>
      <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Live Event Feed</CardTitle>
        {autoRefresh && (
          <div className="flex items-center gap-2">
            <Circle className="h-2.5 w-2.5 fill-success text-success animate-pulse" />
            <span className="text-xs text-muted-foreground">Live</span>
            {newEventsCount > 0 && (
              <Badge variant="destructive">{newEventsCount} new</Badge>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent>
        {connected && (
          <Alert variant="success" className="mb-3">
            <Wifi />
            <AlertDescription>Real-time updates enabled</AlertDescription>
          </Alert>
        )}

        {combinedEvents.length === 0 ? (
          <Alert variant="info">
            <AlertDescription>Waiting for events...</AlertDescription>
          </Alert>
        ) : (
          <ul className="max-h-[350px] overflow-auto flex flex-col gap-2">
            {combinedEvents.map((activity, index) => (
              <li key={index} className="flex items-center gap-3 py-1">
                <div className="flex-shrink-0">
                  {getEventIcon(activity.type || activity.event_type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="truncate text-sm text-foreground">
                    {activity.description || activity.incident_type || 'Event'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {dayjs(activity.timestamp).fromNow()}
                  </p>
                </div>
                {(activity.risk_score > 0 || activity.total_risk_score > 0) && (
                  <Badge
                    variant={getRiskVariant(activity.risk_score || activity.total_risk_score)}
                  >
                    {activity.risk_score || activity.total_risk_score}
                  </Badge>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

export default LiveEventFeed;
